import InputAdornment from '@mui/material/InputAdornment';
import { Input } from './styles';
import type { ReactNode } from 'react';
import type { SxProps } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import FormHelperText from '@mui/material/FormHelperText';
import { useTheme } from '@mui/material/styles';

interface InputProps<T = string> {
  placeholder?: string;
  type?: string;
  startIcon?: React.ReactNode | string;
  endIcon?: React.ReactNode | string;
  width?: string;
  disabled?: boolean;
  multiline?: boolean;
  label?: string;
  value?: T;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  id?: string;
  name?: string;
  error?: boolean;
  helperText?: ReactNode;
  sx?: SxProps<Theme>;
  shadow?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

function TextField<T = string>({
  placeholder,
  type = 'text',
  startIcon = '',
  endIcon = '',
  width = '100%',
  disabled = false,
  multiline = false,
  label = '',
  id,
  name,
  value = '' as T,
  onChange,
  error = false,
  sx,
  shadow = false,
  helperText,
  onKeyDown,
}: InputProps<T>) {
  const theme = useTheme();

  return (
    <Stack gap="4px" width={width ? width : '100%'}>
      <Input
        label={label}
        placeholder={placeholder}
        type={type}
        size="small"
        multiline={multiline}
        onChange={onChange}
        InputProps={{
          startAdornment: startIcon ? (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ) : undefined,
          endAdornment: endIcon ? (
            <InputAdornment position="start">{endIcon}</InputAdornment>
          ) : undefined,
        }}
        sx={{
          width: width,
          pointerEvents: disabled ? 'none' : 'auto',
          ...sx,
        }}
        color={theme.palette.white['01']}
        disabled={disabled}
        value={value}
        id={id}
        name={name}
        error={error}
        shadow={shadow}
        onKeyDown={onKeyDown}
      />
      {helperText && error && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </Stack>
  );
}

export default TextField;
